import { useRef, useState } from "react";
import { FiCalendar } from "react-icons/fi";
import dayjs from "newui/utils/dayjs";
import { FormLabel } from "../FormLabel";
import FormikFieldError from "../FormikFieldError";
import { Popover } from "components/Popover/Popover";
import DatePicker from "components/DatePicker/DatePicker";
import { getInputClasses, getInputWrapperClasses } from "./inputStyles";
import InfoTooltip from "newui/components/InfoTooltip";

export interface BaseDateInputWithLabelProps {
  name?: string;
  className?: string;
  disabled?: boolean;
  errorText?: string;
  guideLine?: string;
  hasError?: boolean;
  label?: string;
  minDate?: Date;
  minDateTooltipContent?: string;
  placeholder?: string;
  required?: boolean;
  testId?: string;
  value?: Date | null;
  onChange?: (date: Date | null) => void;
}

export const DateInputWithLabel: React.FC<BaseDateInputWithLabelProps> = ({
  className = "",
  disabled = false,
  errorText,
  guideLine,
  hasError = false,
  label,
  minDate,
  minDateTooltipContent,
  name,
  placeholder = "Select Date",
  required,
  testId,
  value,
  onChange,
}) => {
  const [showDate, setShowDate] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);

  const hasInputError = !!errorText || hasError;

  const renderDate = value
    ? dayjs(value).format("MMM D, YYYY")
    : placeholder;

  const handleApply = (date: Date | null, close: boolean) => {
    if (onChange) {
      onChange(date);
    }
    if (close) {
      setShowDate(false);
    }
  };

  const handleCancel = () => {
    setShowDate(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }
    if (event.key === "Enter" || event.key === "ArrowDown") {
      setShowDate(true);
    }
  };

  return (
    <div
      className={getInputWrapperClasses(hasInputError, false, className)}
      data-testid={testId}
    >
      {label && (
        <FormLabel
          htmlFor={name}
          label={label}
          required={required}
        >
          {guideLine && (
            <div className="inline-block">
              <InfoTooltip tooltipText={guideLine} />
            </div>
          )}
        </FormLabel>
      )}

      <Popover
        isOpen={showDate}
        closeHandler={() => setShowDate(false)}
        openHandler={() => !disabled && setShowDate(true)}
        placement="bottom-start"
        offset={[0, 0]}
        keyFor={name}
      >
        <div
          ref={inputRef}
          onKeyDown={handleKeyDown}
          className={getInputClasses(hasInputError, "h-14 max-h-14 flex items-center gap-3 cursor-pointer")}
          style={{
            pointerEvents: disabled ? "none" : "auto",
            backgroundColor: disabled ? "#f9f9f9" : "white",
          }}
          data-testid={testId ? `${testId}-trigger` : undefined}
          tabIndex={disabled ? -1 : 0}
        >
          <FiCalendar className="text-blue-500" size={20} />
          <span className={value ? "text-gray-700" : "text-gray-400"}>
            {renderDate}
          </span>
        </div>
        <div>
          <DatePicker
            dates={value || dayjs().toDate()}
            minDate={minDate}
            minDateTooltipContent={minDateTooltipContent}
            className="top-1 w-max"
            onApply={handleApply}
            onCancel={handleCancel}
          />
        </div>
      </Popover>

      <FormikFieldError text={errorText} />
    </div>
  );
};
